import dayjs from 'dayjs'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import {
  BILLING_DISPLAY_TIMEZONE,
  BILLING_ENTITY_OPTIONS,
  BILLING_STATUS_VALUES,
  type BillingEntityValue,
  type BillingStatusValue,
} from '@/pages/Billing/utils'
import { type CasePaymentStatus, type SubmissionRoute } from '@/model/middleware-types'

dayjs.extend(utc)
dayjs.extend(timezone)

// Visit Status filter options: FHIR Appointment.status values behind user-facing
// labels (mirrors the legacy pages/Billing mapping).
export const VISIT_STATUS_OPTIONS = [
  { value: 'booked', label: 'Confirmed' },
  { value: 'pending', label: 'Pending' },
  { value: 'noshow', label: 'No Show' },
  { value: 'fulfilled', label: 'Completed' },
] as const

export type VisitStatusValue = (typeof VISIT_STATUS_OPTIONS)[number]['value']

const VISIT_STATUS_VALUES = VISIT_STATUS_OPTIONS.map((option) => option.value)

// Labels for every payment status the ledger can hold — used to render a case's
// current status.
export const PAYMENT_STATUS_OPTIONS: ReadonlyArray<{ value: CasePaymentStatus; label: string }> = [
  { value: 'no_payment', label: 'No payment' },
  { value: 'awaiting_payer', label: 'Awaiting payer' },
  { value: 'ready_to_process', label: 'Ready to process' },
  { value: 'partially_paid', label: 'Partially paid' },
  { value: 'paid', label: 'Paid' },
  { value: 'failed', label: 'Failed' },
]

// `ready_to_process` lost its only writer when the EOB-save endpoint was removed, so it
// is not offered as a filter — the bucket is always empty. The label above stays for any
// historical row that still carries the value.
export const PAYMENT_STATUS_FILTER_OPTIONS = PAYMENT_STATUS_OPTIONS.filter(
  (option) => option.value !== 'ready_to_process',
)

const PAYMENT_STATUS_FILTER_VALUES = PAYMENT_STATUS_FILTER_OPTIONS.map((option) => option.value)

// Routing filter options: how the claim leaves (or will leave) the building.
export const ROUTING_OPTIONS: ReadonlyArray<{ value: SubmissionRoute; label: string }> = [
  { value: 'bridge', label: 'Bridge' },
  { value: 'stedi', label: 'Stedi' },
  { value: 'manual', label: 'Manual' },
  { value: 'none', label: 'Not routed' },
]

const ROUTING_VALUES = ROUTING_OPTIONS.map((option) => option.value)

// Progress Note filter (legacy parity): a user-facing derivation of Encounter.status —
// submitted = finished, not submitted = still open. The mapping mirrors the legacy
// list-encounters handler's progressNoteStatus translation.
export const PROGRESS_NOTE_OPTIONS = [
  { value: 'submitted', label: 'Submitted' },
  { value: 'not-submitted', label: 'Not Submitted' },
] as const

export type ProgressNoteValue = (typeof PROGRESS_NOTE_OPTIONS)[number]['value']

const PROGRESS_NOTE_VALUES = PROGRESS_NOTE_OPTIONS.map((option) => option.value)

const PROGRESS_NOTE_ENCOUNTER_STATUSES: Record<ProgressNoteValue, string[]> = {
  submitted: ['finished'],
  'not-submitted': ['planned', 'arrived', 'in-progress'],
}

const BILLING_ENTITY_VALUES = BILLING_ENTITY_OPTIONS.map((option) => option.value)

export type BillingV2Filters = {
  visitStatuses: VisitStatusValue[]
  billingEntities: BillingEntityValue[]
  progressNoteStatuses: ProgressNoteValue[]
  claimStatuses: BillingStatusValue[]
  paymentStatuses: CasePaymentStatus[]
  submissionRoutes: SubmissionRoute[]
  /** submit_last_error IS NOT NULL — the Bridge TRIAGE/blocked-claim alarm. */
  needsAttention: boolean
  /** Date-only string (YYYY-MM-DD) in the billing display timezone. Required. */
  dateFrom: string
  /** Date-only string (YYYY-MM-DD) in the billing display timezone. Required. */
  dateTo: string
}

export const BILLING_V2_FILTER_STORAGE_KEY = 'billing-v2-filter-data'

export const BILLING_V2_FILTERS_COLLAPSED_KEY = 'billing-v2-filters-collapsed'

export const BILLING_V2_PAGE_SIZE = 50

// Mirror of the backend list contract's max range. Source of truth:
// packages/middleware/src/billing-v2/handlers/list-cases.contract.ts
export const BILLING_V2_MAX_RANGE_MONTHS = 12

const DATE_ONLY_REGEX = /^\d{4}-\d{2}-\d{2}$/

export function getDefaultFilters(): BillingV2Filters {
  const today = dayjs().tz(BILLING_DISPLAY_TIMEZONE)
  return {
    visitStatuses: [],
    billingEntities: [],
    progressNoteStatuses: [],
    claimStatuses: [],
    paymentStatuses: [],
    submissionRoutes: [],
    needsAttention: false,
    dateFrom: today.subtract(3, 'month').format('YYYY-MM-DD'),
    dateTo: today.format('YYYY-MM-DD'),
  }
}

function sanitizeValues<T extends string>(raw: unknown, allowed: readonly T[]): T[] {
  if (!Array.isArray(raw)) return []
  return raw.filter((value): value is T => (allowed as readonly unknown[]).includes(value))
}

function sanitizeDate(raw: unknown, fallback: string): string {
  return typeof raw === 'string' && DATE_ONLY_REGEX.test(raw) ? raw : fallback
}

export function readPersistedFilters(): BillingV2Filters {
  const defaults = getDefaultFilters()
  try {
    const raw = localStorage.getItem(BILLING_V2_FILTER_STORAGE_KEY)
    if (!raw) return defaults
    const parsed = JSON.parse(raw) as Partial<Record<keyof BillingV2Filters, unknown>>
    return {
      visitStatuses: sanitizeValues(parsed.visitStatuses, VISIT_STATUS_VALUES),
      billingEntities: sanitizeValues(parsed.billingEntities, BILLING_ENTITY_VALUES),
      progressNoteStatuses: sanitizeValues(parsed.progressNoteStatuses, PROGRESS_NOTE_VALUES),
      claimStatuses: sanitizeValues(parsed.claimStatuses, BILLING_STATUS_VALUES),
      paymentStatuses: sanitizeValues(parsed.paymentStatuses, PAYMENT_STATUS_FILTER_VALUES),
      submissionRoutes: sanitizeValues(parsed.submissionRoutes, ROUTING_VALUES),
      needsAttention: parsed.needsAttention === true,
      dateFrom: sanitizeDate(parsed.dateFrom, defaults.dateFrom),
      dateTo: sanitizeDate(parsed.dateTo, defaults.dateTo),
    }
  } catch {
    return defaults
  }
}

export function persistFilters(filters: BillingV2Filters): void {
  try {
    localStorage.setItem(BILLING_V2_FILTER_STORAGE_KEY, JSON.stringify(filters))
  } catch {
    // ignore quota/serialization errors
  }
}

/** How many filters differ from the defaults — feeds the collapsed toggle's badge. */
export function countActiveFilters(filters: BillingV2Filters): number {
  const defaults = getDefaultFilters()
  return (
    filters.visitStatuses.length +
    filters.billingEntities.length +
    filters.progressNoteStatuses.length +
    filters.claimStatuses.length +
    filters.paymentStatuses.length +
    filters.submissionRoutes.length +
    (filters.needsAttention ? 1 : 0) +
    (filters.dateFrom !== defaults.dateFrom ? 1 : 0) +
    (filters.dateTo !== defaults.dateTo ? 1 : 0)
  )
}

export function readPersistedFiltersCollapsed(): boolean {
  try {
    return localStorage.getItem(BILLING_V2_FILTERS_COLLAPSED_KEY) === 'true'
  } catch {
    return false
  }
}

export function persistFiltersCollapsed(collapsed: boolean): void {
  try {
    localStorage.setItem(BILLING_V2_FILTERS_COLLAPSED_KEY, String(collapsed))
  } catch {
    // ignore quota errors
  }
}

function toStartOfDayInstant(date: string): dayjs.Dayjs {
  return dayjs.tz(`${date} 00:00:00`, BILLING_DISPLAY_TIMEZONE)
}

function toEndOfDayInstant(date: string): dayjs.Dayjs {
  return dayjs.tz(`${date} 23:59:59.999`, BILLING_DISPLAY_TIMEZONE)
}

// Validates the same instants buildListInput sends, so anything that passes here
// also passes the backend contract's range check — and nothing valid is refused: the
// 12 months are added exactly as the contract does (adding them in New York time
// disagreed by a day on month-end and DST edges).
export function getDateRangeError(
  filters: Pick<BillingV2Filters, 'dateFrom' | 'dateTo'>,
): string | null {
  const from = toStartOfDayInstant(filters.dateFrom)
  const to = toEndOfDayInstant(filters.dateTo)
  if (!from.isValid() || !to.isValid()) {
    return 'Select a valid date range'
  }
  if (to.isBefore(from)) {
    return 'End date must not be before the start date'
  }
  // Same arithmetic as the contract (Date#setUTCMonth): a month-end overflow rolls into the
  // next month there, where dayjs would clamp it.
  const max = from.toDate()
  max.setUTCMonth(max.getUTCMonth() + BILLING_V2_MAX_RANGE_MONTHS)
  if (to.toDate() > max) {
    return `Date range must not exceed ${BILLING_V2_MAX_RANGE_MONTHS} months`
  }
  return null
}

export type BillingV2ListInput = {
  dateFrom: string
  dateTo: string
  patientId?: string
  claimStatus?: BillingStatusValue[]
  paymentStatus?: CasePaymentStatus[]
  submissionRoute?: SubmissionRoute[]
  billingEntity?: BillingEntityValue[]
  appointmentStatus?: VisitStatusValue[]
  encounterStatus?: string[]
  needsAttention?: boolean
  count: number
  offset: number
}

/** Selected Progress Note values → the FHIR Encounter.status set the backend filters on. */
export function progressNoteEncounterStatuses(selected: ProgressNoteValue[]): string[] {
  return [...new Set(selected.flatMap((value) => PROGRESS_NOTE_ENCOUNTER_STATUSES[value]))]
}

export function buildListInput(
  filters: BillingV2Filters,
  patientId: string | null,
  offset: number,
): BillingV2ListInput {
  const encounterStatus = progressNoteEncounterStatuses(filters.progressNoteStatuses)
  return {
    dateFrom: toStartOfDayInstant(filters.dateFrom).toISOString(),
    dateTo: toEndOfDayInstant(filters.dateTo).toISOString(),
    ...(patientId ? { patientId } : {}),
    ...(filters.claimStatuses.length ? { claimStatus: filters.claimStatuses } : {}),
    ...(filters.paymentStatuses.length ? { paymentStatus: filters.paymentStatuses } : {}),
    ...(filters.submissionRoutes.length ? { submissionRoute: filters.submissionRoutes } : {}),
    ...(filters.billingEntities.length ? { billingEntity: filters.billingEntities } : {}),
    ...(filters.visitStatuses.length ? { appointmentStatus: filters.visitStatuses } : {}),
    ...(encounterStatus.length ? { encounterStatus } : {}),
    ...(filters.needsAttention ? { needsAttention: true } : {}),
    count: BILLING_V2_PAGE_SIZE,
    offset,
  }
}
